import { Link } from 'react-router-dom';
import styles from './About.module.css';

const TEAM = [
  {
    name: 'staticpayload',
    role: 'Maintainer',
    description: 'Software engineer with background in computational physics and numerical methods. Built AxLabs to address the gap between entertainment-focused educational software and research-grade simulation tools.',
  },
];

const HISTORY = [
  {
    year: '2022',
    title: 'Inception',
    description: 'AxLabs conceived as infrastructure for serious science education. Recognition that existing platforms prioritize engagement over correctness.',
  },
  {
    year: '2023',
    title: 'Core Development',
    description: 'Simulation engine built from first principles. Focus on numerical stability, reproducibility, and extensibility. Early experiments in mechanics and circuits.',
  },
  {
    year: '2024',
    title: 'Public Release',
    description: 'Platform opens to educational institutions. Expansion into chemistry, biology, and mathematics. Documentation and validation frameworks established.',
  },
];

const PRINCIPLES = [
  {
    title: 'Infrastructure Over Product',
    description: 'AxLabs is designed to operate for decades. API contracts prioritize stability over features. Breaking changes are avoided. Data formats use open standards.',
  },
  {
    title: 'Correctness Over Convenience',
    description: 'We do not simplify physics for easier visualization. We do not hide numerical error. Students encounter genuine complexity and learn to work with it.',
  },
  {
    title: 'Transparency Over Magic',
    description: 'All methods are documented. Assumptions are stated. Limitations are communicated. Nothing is hidden behind "the computer said so."',
  },
  {
    title: 'Community Over Control',
    description: 'AxLabs is open infrastructure. Educators can extend, modify, and host. We provide the foundation; the community builds upon it.',
  },
];

export function About() {
  return (
    <div className={styles.about}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>About</h1>
          <p className={styles.subtitle}>
            AxLabs is research-grade infrastructure for science education.
            Built for institutions that value correctness, reproducibility, and long-term stability.
          </p>
        </div>
      </header>

      {/* What Is AxLabs */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What AxLabs Is</h2>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              AxLabs is a collection of deterministic science simulations covering physics, chemistry,
              biology, and mathematics. Each experiment solves real governing equations with documented
              numerical methods and reproducible outputs.
            </p>
            <p className={styles.proseText}>
              Unlike educational games that prioritize engagement over accuracy, AxLabs is designed for
              legitimate scientific inquiry. Students can explore parameter space, compare results to
              theoretical predictions, and export data for further analysis. Instructors can design
              assessments with objectively correct answers.
            </p>
            <p className={styles.proseText}>
              AxLabs is infrastructure, not a product. It is built to be maintained for decades,
              not pivoted based on growth metrics. Every decision prioritizes correctness and
              longevity.
            </p>
          </div>
        </div>
      </section>

      {/* What AxLabs Is Not */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What AxLabs Is Not</h2>
          <div className={styles.notList}>
            <div className={styles.notItem}>
              <span className={styles.notIcon}>×</span>
              <div>
                <h3 className={styles.notTitle}>Not a Game</h3>
                <p className={styles.notDesc}>
                  There are no points, no badges, no leaderboards. Learning is its own reward.
                  Engagement comes from genuine understanding, not psychological manipulation.
                </p>
              </div>
            </div>
            <div className={styles.notItem}>
              <span className={styles.notIcon}>×</span>
              <div>
                <h3 className={styles.notTitle}>Not a Tutorial</h3>
                <p className={styles.notDesc}>
                  Experiments do not guide students toward correct answers. Failure is informative.
                  Students must interpret results and identify their own misconceptions.
                </p>
              </div>
            </div>
            <div className={styles.notItem}>
              <span className={styles.notIcon}>×</span>
              <div>
                <h3 className={styles.notTitle}>Not a Replacement for Theory</h3>
                <p className={styles.notDesc}>
                  Simulations complement, not replace, theoretical understanding. Students must
                  derive equations, make predictions, and then test them experimentally.
                </p>
              </div>
            </div>
            <div className={styles.notItem}>
              <span className={styles.notIcon}>×</span>
              <div>
                <h3 className={styles.notTitle}>Not Venture-Backed</h3>
                <p className={styles.notDesc}>
                  AxLabs has no growth targets, no user acquisition metrics, no pressure to monetize
                  attention. Long-term correctness matters more than short-term scale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OmenSystems Relationship */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>OmenSystems Relationship</h2>
          <div className={styles.relationshipCard}>
            <p className={styles.relationshipText}>
              AxLabs is a subsidiary of <strong>OmenSystems.com</strong>, a software infrastructure
              company focused on long-term, maintainable systems. This relationship provides stability
              and independence.
            </p>
            <p className={styles.relationshipText}>
              OmenSystems operates on infrastructure timescales—decades, not quarters. AxLabs inherits
              this philosophy. The platform will be maintained as long as it serves educational needs.
              There is no exit strategy.
            </p>
            <p className={styles.relationshipText}>
              Operational independence allows AxLabs to make decisions based on pedagogical value,
              not commercial pressure. Features are added when they improve learning outcomes,
              not when they drive engagement metrics.
            </p>
          </div>
        </div>
      </section>

      {/* Maintainer */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Maintainer</h2>
          <div className={styles.teamList}>
            {TEAM.map((member) => (
              <div key={member.name} className={styles.teamCard}>
                <div className={styles.teamAvatar}>
                  <span className={styles.teamInitial}>{member.name[0]}</span>
                </div>
                <div>
                  <h3 className={styles.teamName}>{member.name}</h3>
                  <p className={styles.teamRole}>{member.role}</p>
                  <p className={styles.teamDesc}>{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>History</h2>
          <div className={styles.timeline}>
            {HISTORY.map((event) => (
              <div key={event.year} className={styles.timelineItem}>
                <span className={styles.timelineYear}>{event.year}</span>
                <div className={styles.timelineContent}>
                  <h3 className={styles.timelineTitle}>{event.title}</h3>
                  <p className={styles.timelineDesc}>{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Philosophy */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Technical Philosophy</h2>
          <div className={styles.principlesGrid}>
            {PRINCIPLES.map((principle) => (
              <div key={principle.title} className={styles.principleCard}>
                <h3 className={styles.principleTitle}>{principle.title}</h3>
                <p className={styles.principleDesc}>{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-Term Commitment */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Long-Term Commitment</h2>
          <div className={styles.commitmentBox}>
            <p className={styles.commitmentText}>
              AxLabs makes the following commitments to the educational community:
            </p>
            <ul className={styles.commitmentList}>
              <li className={styles.commitmentItem}>
                <strong>Perpetual free access</strong> for core simulation functionality
              </li>
              <li className={styles.commitmentItem}>
                <strong>Data portability</strong>—all experiments support export to open formats
              </li>
              <li className={styles.commitmentItem}>
                <strong>API stability</strong>—breaking changes minimized and documented
              </li>
              <li className={styles.commitmentItem}>
                <strong>Transparent methodology</strong>—all algorithms and assumptions published
              </li>
              <li className={styles.commitmentItem}>
                <strong>Independence from engagement metrics</strong>—features driven by pedagogy
              </li>
              <li className={styles.commitmentItem}>
                <strong>Open source option</strong>—institutions can self-host and extend
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Link to="/philosophy" className={styles.footerLink}>Read Philosophy</Link>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/research" className={styles.footerLink}>Research & Validation</Link>
          <span className={styles.footerSeparator}>·</span>
          <a href="https://omensystems.com" className={styles.footerLink} target="_blank" rel="noopener noreferrer">OmenSystems.com</a>
        </div>
      </footer>
    </div>
  );
}
